<template>
    <pbl-button
        slot="context"
        :loading="isUploading"
        variant="primary"
        type="button"
        compact
        class="button"
        @click="onButtonClick"
    >
        <Component
            :is="modelValue ? Trash : Paperclip"
            slot="icon"
            class="button__icon"
        />
        {{ modelValue ? (modelValue as File).name : t('common.attachFile') }}
    </pbl-button>
    <input
        ref="inputRef"
        :value="modelValue?.webkitRelativePath"
        type="file"
        class="hidden"
        @change="onFileChange"
    />
</template>

<script setup lang="ts">
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { oneMBInBytes, validateFileSize } from '@/utils/file-utils'
import { Trash, Paperclip } from 'lucide-vue-next'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    modelValue: File | undefined

    isUploading?: boolean
    invalid?: boolean
    errorMessage?: string
    maxFileSizeInBytes?: number
}>()

const { t } = useI18n()
const toasts = usePebbleToasts()
const inputRef = ref<HTMLInputElement | undefined>(undefined)

const emit = defineEmits<{
    'update:modelValue': [value: File | undefined],
    'deleteFile': [],
}>()

const onButtonClick = (event: CustomEvent) => {
    event.preventDefault()
    if (props.isUploading) {
        return
    }
    if (props.modelValue) {
        emit('deleteFile')
    } else {
        inputRef.value?.click()
    }
}

const onFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]
    if (!file) {
        return
    }
    if (props.maxFileSizeInBytes && !validateFileSize(file, props.maxFileSizeInBytes)) {
        const maxFileSizeInMB = props.maxFileSizeInBytes / oneMBInBytes
        toasts?.error({
            message: t('common.errors.fileTooLarge', {maxFileSizeInMB}),
        })
        return
    }
    emit('update:modelValue', file)
}
</script>

<style scoped lang="scss">
.button {
    font-size: $default-font-size;

    &__icon {
        height: 18px;
        width: 18px;
    }
}
.hidden {
    display: none;
}
</style>
